import React, { useEffect, useState, useMemo } from 'react';
import { Box, Card, CardContent, CircularProgress, Typography, Chip, Grid, Stack, Button, Divider, Alert, TextField, IconButton, FormControl, InputLabel, Select, MenuItem, FormHelperText, List, ListItem, ListItemIcon, ListItemText, Accordion, AccordionSummary, AccordionDetails, Dialog, DialogTitle, DialogContent, DialogActions, DialogContentText } from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import SportsSoccerIcon from '@mui/icons-material/SportsSoccer';
import GroupsIcon from '@mui/icons-material/Groups';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import EditIcon from '@mui/icons-material/Edit';
import axios from 'axios';
import config from '../../config/config.json';

const host = config.production.host;
const getJwt = () => localStorage.getItem('feathers-jwt');

function parseDateOnly(str) {
  if (!str) return null;
  const [y, m, d] = String(str).split('-').map(n => parseInt(n, 10));
  if (!y || !m || !d) return new Date(str);
  return new Date(y, m - 1, d);
}

function formatDate(d) {
  if (!d) return '-';
  return d.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

export default function GiornataDetailPage() {
  const { giornataId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [giornata, setGiornata] = useState(null);
  const [partite, setPartite] = useState([]);
  const [teamsMap, setTeamsMap] = useState(new Map());
  const [teams, setTeams] = useState([]);
  const [legaName, setLegaName] = useState('');
  const [stagioneName, setStagioneName] = useState('');

  // Form stato: default e righe blocchi
  const [defData, setDefData] = useState(''); // YYYY-MM-DD
  const [defOra, setDefOra] = useState('');   // HH:MM
  const [defLuogo, setDefLuogo] = useState('');
  const [defNote, setDefNote] = useState('');
  const [rows, setRows] = useState([{ teamCasaId: '', teamOspiteId: '', data: '', ora: '', luogo: '', note: '' }]);

  // Stato per edit singola partita
  const [editMatch, setEditMatch] = useState(null);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);

  // Spostato useMemo qui per evitare "Rendered fewer hooks than expected"
  // Deve stare prima di qualsiasi return condizionale
  const unavailableTeamsForEdit = useMemo(() => {
    if (!editMatch) return new Set();
    const s = new Set();
    partite.forEach(p => {
      // Skip the match being edited
      if (p.id === editMatch.id) return;
      if (p.teamCasaId) s.add(Number(p.teamCasaId));
      if (p.teamOspiteId) s.add(Number(p.teamOspiteId));
    });
    return s;
  }, [partite, editMatch]);



  const sDate = useMemo(() => parseDateOnly(giornata?.dataInizio || giornata?.data_inizio || giornata?.datainizio), [giornata]);
  const eDate = useMemo(() => parseDateOnly(giornata?.dataFine || giornata?.data_fine || giornata?.datafine) || sDate, [giornata, sDate]);

  // Set squadre già presenti in giornata (da partite esistenti)
  const usedTeamsExisting = useMemo(() => {
    const s = new Set();
    (partite || []).forEach(p => {
      if (p.teamCasaId) s.add(Number(p.teamCasaId));
      if (p.teamOspiteId) s.add(Number(p.teamOspiteId));
    });
    return s;
  }, [partite]);

  const isTeamUsedExisting = (id) => usedTeamsExisting.has(Number(id));
  const isTeamUsedInOtherRows = (id, idx) => rows.some((r, i) => i !== idx && (String(r.teamCasaId) === String(id) || String(r.teamOspiteId) === String(id)));

  // Set squadre mancanti (non ancora assegnate)
  const missingTeams = useMemo(() => {
    return teams.filter(t => !usedTeamsExisting.has(t.id));
  }, [teams, usedTeamsExisting]);

  // Conflitti aggregati per UI
  const conflicts = useMemo(() => {
    const msgs = [];
    if (rows.some(r => r.teamCasaId && r.teamOspiteId && String(r.teamCasaId) === String(r.teamOspiteId))) {
      msgs.push('Casa e Ospite coincidono in almeno una riga');
    }
    if (rows.some(r => (r.teamCasaId && isTeamUsedExisting(r.teamCasaId)) || (r.teamOspiteId && isTeamUsedExisting(r.teamOspiteId)))) {
      msgs.push('Una o più squadre sono già assegnate in questa giornata');
    }
    if (rows.some((r, idx) => (r.teamCasaId && isTeamUsedInOtherRows(r.teamCasaId, idx)) || (r.teamOspiteId && isTeamUsedInOtherRows(r.teamOspiteId, idx)))) {
      msgs.push('La stessa squadra è selezionata in più righe');
    }
    return msgs;
  }, [rows, usedTeamsExisting]);

  const hasConflicts = conflicts.length > 0;

  const loadAll = async () => {
    try {
      setLoading(true);
      const jwt = getJwt();

      // 1. Fetch Giornata first to get legaId
      const gResp = await axios.get(`${host}/tbl-giornata/${giornataId}`, { headers: { Authorization: `Bearer ${jwt}` } });
      const currentGiornata = gResp.data;
      setGiornata(currentGiornata);

      const currentLegaId = currentGiornata?.legaId;
      const currentStagioneId = currentGiornata?.stagioneId;

      // 2. Fetch dependencies (Teams filtered by legaId)
      const promiseRequests = [
        axios.get(`${host}/tbl-partita`, { headers: { Authorization: `Bearer ${jwt}` }, params: { $limit: 1000, giornataId } }),
        axios.get(`${host}/tbl-team`, { headers: { Authorization: `Bearer ${jwt}` }, params: { $limit: 1000, $sort: { nome: 1 }, legaId: currentLegaId } })
      ];

      // Optional: Fetch names
      const pLega = currentLegaId ? axios.get(`${host}/tbl-lega/${currentLegaId}`, { headers: { Authorization: `Bearer ${jwt}` } }) : Promise.resolve(null);
      const pStagione = currentStagioneId ? axios.get(`${host}/tbl-stagione/${currentStagioneId}`, { headers: { Authorization: `Bearer ${jwt}` } }) : Promise.resolve(null);

      promiseRequests.push(pLega);
      promiseRequests.push(pStagione);

      const [pResp, tResp, lResp, sResp] = await Promise.all(promiseRequests);

      setPartite(pResp.data?.data || []);

      const teamsArr = (tResp.data?.data || []);
      setTeams(teamsArr);
      const tmap = new Map();
      teamsArr.forEach(t => tmap.set(t.id, t.nome));
      setTeamsMap(tmap);

      if (lResp?.data) setLegaName(lResp.data.nome || `Lega ${currentLegaId}`);
      if (sResp?.data) setStagioneName(sResp.data.nome || `Stagione ${currentStagioneId}`);

    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadAll(); /* eslint-disable-next-line */ }, [giornataId]);

  if (loading) {
    return (
      <Box sx={{ p: { xs: 2, md: 4 }, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!giornata) {
    return (
      <Box sx={{ p: { xs: 2, md: 4 } }}>
        <Alert severity="error">Giornata non trovata</Alert>
      </Box>
    );
  }

  const titolo = giornata.nome || `Giornata ${giornata.numero ?? ''}`;
  const statoChip = (
    <Chip size="small" label={giornata.flDisputata ? 'Disputata' : 'Da disputare'} color={giornata.flDisputata ? 'success' : 'warning'} />
  );

  const addRow = () => {
    setRows(prev => prev.length >= 30 ? prev : [...prev, { teamCasaId: '', teamOspiteId: '', data: '', ora: '', luogo: '', note: '' }]);
  };

  const removeRow = (idx) => {
    setRows(prev => prev.filter((_, i) => i !== idx));
  };

  const updateRow = (idx, field, value) => {
    setRows(prev => prev.map((r, i) => {
      if (i !== idx) return r;
      const next = { ...r, [field]: value };
      // Evita che casa e ospite coincidano nella stessa riga
      if (field === 'teamCasaId' && value && String(value) === String(r.teamOspiteId || '')) {
        next.teamOspiteId = '';
      }
      if (field === 'teamOspiteId' && value && String(value) === String(r.teamCasaId || '')) {
        next.teamCasaId = '';
      }
      return next;
    }));
  };

  const handleSubmit = async () => {
    try {
      if (!giornata?.legaId || !giornata?.stagioneId) {
        alert('Lega o Stagione mancanti nella giornata');
        return;
      }
      // Validazione: nessuna riga con stesso team casa/ospite
      const invalidSameTeam = rows.some(r => r.teamCasaId && r.teamOspiteId && String(r.teamCasaId) === String(r.teamOspiteId));
      if (invalidSameTeam) {
        alert('Errore: in una o più righe il Team casa coincide con il Team ospite. Correggi e riprova.');
        return;
      }
      // Validazione: team non già assegnati nella giornata
      const conflictExisting = rows.some(r => (r.teamCasaId && isTeamUsedExisting(r.teamCasaId)) || (r.teamOspiteId && isTeamUsedExisting(r.teamOspiteId)));
      if (conflictExisting) {
        alert('Errore: una o più squadre sono già assegnate a partite della stessa giornata. Rimuovere o cambiare la selezione.');
        return;
      }
      // Validazione: team univoci tra le righe in input
      const conflictRows = rows.some((r, idx) => (r.teamCasaId && isTeamUsedInOtherRows(r.teamCasaId, idx)) || (r.teamOspiteId && isTeamUsedInOtherRows(r.teamOspiteId, idx)));
      if (conflictRows) {
        alert('Errore: la stessa squadra è selezionata in più righe. Ogni squadra può comparire una sola volta nel batch.');
        return;
      }
      const jwt = localStorage.getItem('feathers-jwt');
      const payload = {
        legaId: giornata.legaId,
        stagioneId: giornata.stagioneId,
        giornataId: giornata.id,
        data_partita: defData || null,
        ore_partita: defOra ? `${defOra}:00` : null,
        luogo: defLuogo || null,
        note: defNote || null,
        blocchi: rows
          .filter(r => r.teamCasaId && r.teamOspiteId)
          .map(r => ({
            teamCasaId: Number(r.teamCasaId),
            teamOspiteId: Number(r.teamOspiteId),
            data_partita: r.data || null,
            ore_partita: r.ora ? `${r.ora}:00` : null,
            luogo: r.luogo || null,
            note: r.note || null
          }))
      };

      const resp = await axios.post(`${host}/proc-crea-partite-giornata`, payload, {
        headers: { Authorization: `Bearer ${jwt}` }
      });
      const esito = resp.data?.esito;
      if (Number(esito) === 1) {
        // reload partite
        await loadAll();
        // svuota gli input dopo successo
        setDefData('');
        setDefOra('');
        setDefLuogo('');
        setDefNote('');
        setRows([{ teamCasaId: '', teamOspiteId: '', data: '', ora: '', luogo: '', note: '' }]);
        alert(resp.data?.message || 'Partite create');
      } else {
        alert(resp.data?.message || 'Operazione terminata con errori');
      }
    } catch (e) {
      console.error(e);
      alert(e?.response?.data?.message || e.message || 'Errore durante la creazione');
    }
  };



  const handleOpenEdit = (match) => {
    setEditMatch({
      ...match,
      // Fix formati per input type date/time
      data: match.data ? match.data.substring(0, 10) : '',
      ore: match.ore ? match.ore.substring(0, 5) : ''
    });
    setIsEditDialogOpen(true);
  };

  const handleCloseEdit = () => {
    setEditMatch(null);
    setIsEditDialogOpen(false);
  };

  const handleEditChange = (field, value) => {
    setEditMatch(prev => ({ ...prev, [field]: value }));
  };

  const handleUpdateMatch = async () => {
    try {
      if (!editMatch) return;
      const jwt = getJwt();
      const payload = {
        teamCasaId: editMatch.teamCasaId,
        teamOspiteId: editMatch.teamOspiteId,
        data: editMatch.data || null,
        ore: editMatch.ore ? (editMatch.ore.length === 5 ? `${editMatch.ore}:00` : editMatch.ore) : null,
        luogo: editMatch.luogo || null,
        note: editMatch.note || null
      };

      await axios.patch(`${host}/tbl-partita/${editMatch.id}`, payload, {
        headers: { Authorization: `Bearer ${jwt}` }
      });

      alert('Partita aggiornata con successo');
      handleCloseEdit();
      loadAll();
    } catch (e) {
      console.error(e);
      alert('Errore durante l\'aggiornamento');
    }
  };

  const handleDeleteMatch = async (id) => {
    if (!window.confirm('Sei sicuro di voler eliminare questa partita?')) return;
    try {
      const jwt = getJwt();
      await axios.delete(`${host}/tbl-partita/${id}`, {
        headers: { Authorization: `Bearer ${jwt}` }
      });
      alert('Partita eliminata');
      loadAll();
    } catch (e) {
      console.error(e);
      alert('Errore durante l\'eliminazione');
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 4 } }}>
      <Card sx={{ borderRadius: 3, boxShadow: 3, mb: 2 }}>
        <CardContent>
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <CalendarTodayIcon color="primary" />
              <Typography variant="h5" sx={{ fontWeight: 700 }}>{titolo}</Typography>
              {statoChip}
            </Stack>
            <Stack direction="row" spacing={1}>
              <Button variant="outlined" onClick={() => {
                if (giornata && giornata.legaId) {
                  navigate(`/leghe/${giornata.legaId}?tab=giornate`);
                } else {
                  navigate(-1);
                }
              }}>Indietro</Button>
              <Button variant="contained" onClick={() => navigate(`/partite?legaId=${giornata.legaId}`)}>Vedi partite</Button>
            </Stack>
          </Stack>
          <Box sx={{ mt: 1 }}>
            {/* <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Giornata {giornata.numero ?? '-'}</Typography> */}
            <Typography variant="body2">
              Lega: {giornata.legaId ? (
                <a href={`#/leghe/${giornata.legaId}`} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'underline', color: '#1976d2' }}>
                  {legaName || `Lega ${giornata.legaId}`}
                </a>
              ) : '-'}
            </Typography>
            <Typography variant="body2">Stagione: {stagioneName || (giornata.stagioneId ? `Stagione ${giornata.stagioneId}` : '-')}</Typography>
            {/* <Typography variant="body2" color="text.secondary">
              Periodo: {formatDate(sDate)} {eDate && formatDate(eDate) !== formatDate(sDate) ? `→ ${formatDate(eDate)}` : ''}
            </Typography> */}
          </Box>
        </CardContent>
      </Card>
      {/* Assegnazione squadre */}
      <Card sx={{ borderRadius: 2, boxShadow: 2 }}>
        <CardContent>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
            <GroupsIcon color="primary" />
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Assegnazione squadre alla giornata</Typography>
          </Stack>

          {/* Accordion per creazione partite */}
          <Accordion sx={{ mb: 2 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <AddIcon color="primary" />
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Crea nuove partite</Typography>
              </Stack>
            </AccordionSummary>
            <AccordionDetails>
              {/* Defaults globali */}
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>Impostazioni default</Typography>
              <Grid container spacing={1} sx={{ mb: 2 }}>
                <Grid item xs={12} md={3}>
                  <TextField label="Data (default)" type="date" fullWidth size="small" value={defData} onChange={(e) => setDefData(e.target.value)} InputLabelProps={{ shrink: true }} />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField label="Ora (default)" type="time" fullWidth size="small" value={defOra} onChange={(e) => setDefOra(e.target.value)} InputLabelProps={{ shrink: true }} />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField label="Luogo (default)" fullWidth size="small" value={defLuogo} onChange={(e) => setDefLuogo(e.target.value)} />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField label="Note (default)" fullWidth size="small" value={defNote} onChange={(e) => setDefNote(e.target.value)} />
                </Grid>
              </Grid>

              {/* Righe blocchi */}
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>Partite da creare</Typography>
              <Stack spacing={1} sx={{ mb: 1 }}>
                {rows.map((r, idx) => {
                  const hasRowConflict = Boolean(r.teamCasaId && ((String(r.teamCasaId) === String(r.teamOspiteId || '')) || isTeamUsedExisting(r.teamCasaId) || isTeamUsedInOtherRows(r.teamCasaId, idx))) ||
                    Boolean(r.teamOspiteId && ((String(r.teamOspiteId) === String(r.teamCasaId || '')) || isTeamUsedExisting(r.teamOspiteId) || isTeamUsedInOtherRows(r.teamOspiteId, idx)));
                  return (
                    <Card key={idx} variant="outlined" sx={{
                      p: 1.5,
                      bgcolor: hasRowConflict ? 'error.lighter' : 'background.paper',
                      borderColor: hasRowConflict ? 'error.main' : 'divider',
                      transition: 'background-color 0.3s, border-color 0.3s',
                      boxShadow: 1,
                    }}>

                      <Grid container spacing={1} alignItems="center">
                        <Grid item xs={12} md={4}>
                          <FormControl fullWidth size="small" error={Boolean(r.teamCasaId && ((String(r.teamCasaId) === String(r.teamOspiteId || '')) || isTeamUsedExisting(r.teamCasaId) || isTeamUsedInOtherRows(r.teamCasaId, idx)))}>
                            <InputLabel id={`casa-${idx}`}>Team casa</InputLabel>
                            <Select labelId={`casa-${idx}`} label="Team casa" value={r.teamCasaId} onChange={(e) => updateRow(idx, 'teamCasaId', e.target.value)}>
                              <MenuItem value=""><em>-</em></MenuItem>
                              {teams.map(t => (
                                <MenuItem
                                  key={t.id}
                                  value={t.id}
                                  disabled={String(t.id) === String(r.teamOspiteId || '') || isTeamUsedExisting(t.id) || isTeamUsedInOtherRows(t.id, idx)}
                                >{t.nome}</MenuItem>
                              ))}
                            </Select>
                            <FormHelperText>
                              {String(r.teamCasaId) === String(r.teamOspiteId || '') ? 'Casa e Ospite non possono coincidere' :
                                (r.teamCasaId && isTeamUsedExisting(r.teamCasaId)) ? 'Squadra già assegnata in questa giornata' :
                                  (r.teamCasaId && isTeamUsedInOtherRows(r.teamCasaId, idx)) ? 'Squadra già selezionata in un\'altra riga' : ' '}
                            </FormHelperText>
                          </FormControl>
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <FormControl fullWidth size="small" error={Boolean(r.teamOspiteId && ((String(r.teamOspiteId) === String(r.teamCasaId || '')) || isTeamUsedExisting(r.teamOspiteId) || isTeamUsedInOtherRows(r.teamOspiteId, idx)))}>
                            <InputLabel id={`ospite-${idx}`}>Team ospite</InputLabel>
                            <Select labelId={`ospite-${idx}`} label="Team ospite" value={r.teamOspiteId} onChange={(e) => updateRow(idx, 'teamOspiteId', e.target.value)}>
                              <MenuItem value=""><em>-</em></MenuItem>
                              {teams.map(t => (
                                <MenuItem
                                  key={t.id}
                                  value={t.id}
                                  disabled={String(t.id) === String(r.teamCasaId || '') || isTeamUsedExisting(t.id) || isTeamUsedInOtherRows(t.id, idx)}
                                >{t.nome}</MenuItem>
                              ))}
                            </Select>
                            <FormHelperText>
                              {String(r.teamOspiteId) === String(r.teamCasaId || '') ? 'Casa e Ospite non possono coincidere' :
                                (r.teamOspiteId && isTeamUsedExisting(r.teamOspiteId)) ? 'Squadra già assegnata in questa giornata' :
                                  (r.teamOspiteId && isTeamUsedInOtherRows(r.teamOspiteId, idx)) ? 'Squadra già selezionata in un\'altra riga' : ' '}
                            </FormHelperText>
                          </FormControl>
                        </Grid>
                        <Grid item xs={6} md={2}>
                          <TextField label="Data" type="date" fullWidth size="small" value={r.data} onChange={(e) => updateRow(idx, 'data', e.target.value)} InputLabelProps={{ shrink: true }} />
                        </Grid>
                        <Grid item xs={6} md={2}>
                          <TextField label="Ora" type="time" fullWidth size="small" value={r.ora} onChange={(e) => updateRow(idx, 'ora', e.target.value)} InputLabelProps={{ shrink: true }} />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <TextField label="Luogo" fullWidth size="small" value={r.luogo} onChange={(e) => updateRow(idx, 'luogo', e.target.value)} />
                        </Grid>
                        <Grid item xs={12} md={5}>
                          <TextField label="Note" fullWidth size="small" value={r.note} onChange={(e) => updateRow(idx, 'note', e.target.value)} />
                        </Grid>
                        <Grid item xs={12} md={1} sx={{ textAlign: { xs: 'left', md: 'right' } }}>
                          <IconButton color="error" onClick={() => removeRow(idx)} disabled={rows.length === 1}><DeleteOutlineIcon /></IconButton>
                        </Grid>
                      </Grid>
                    </Card>
                  );
                })}
                <Box>
                  <Button startIcon={<AddIcon />} onClick={addRow} disabled={rows.length >= 30}>Aggiungi riga</Button>
                </Box>
              </Stack>

              {hasConflicts && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  {conflicts.map((m, i) => (
                    <div key={i}>{m}</div>
                  ))}
                </Alert>
              )}

              <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                <Box sx={{ flex: 1 }} />
                <Button variant="contained" color="success" onClick={handleSubmit} disabled={hasConflicts}>Crea partite</Button>
              </Stack>
            </AccordionDetails>
          </Accordion>

          {/* Liste dettagliate squadre in accordion */}
          <Grid container spacing={2} sx={{ mb: 2 }}>
            {/* Squadre già assegnate */}
            <Grid item xs={12} md={6}>
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <CheckCircleOutlineIcon color="success" />
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Squadre già assegnate</Typography>
                    <Chip label={usedTeamsExisting.size} color="success" size="small" />
                  </Stack>
                </AccordionSummary>
                <AccordionDetails>
                  {usedTeamsExisting.size === 0 ? (
                    <Typography variant="body2" color="text.secondary">Nessuna squadra ancora assegnata.</Typography>
                  ) : (
                    <List dense sx={{ maxHeight: 300, overflow: 'auto' }}>
                      {Array.from(usedTeamsExisting).map(id => (
                        <ListItem key={id}>
                          <ListItemIcon sx={{ minWidth: 32 }}><CheckCircleOutlineIcon color="success" fontSize="small" /></ListItemIcon>
                          <ListItemText primary={teamsMap.get(id) || `Team ${id}`} />
                        </ListItem>
                      ))}
                    </List>
                  )}
                </AccordionDetails>
              </Accordion>
            </Grid>

            {/* Squadre mancanti */}
            <Grid item xs={12} md={6}>
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <RadioButtonUncheckedIcon color={missingTeams.length === 0 ? 'success' : 'warning'} />
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Mancano da assegnare</Typography>
                    <Chip label={missingTeams.length} color={missingTeams.length === 0 ? 'success' : 'warning'} size="small" />
                  </Stack>
                </AccordionSummary>
                <AccordionDetails>
                  {missingTeams.length === 0 ? (
                    <Typography variant="body2" color="success.main">🎉 Tutte le squadre sono state assegnate!</Typography>
                  ) : (
                    <List dense sx={{ maxHeight: 300, overflow: 'auto' }}>
                      {missingTeams.map(t => (
                        <ListItem key={t.id}>
                          <ListItemIcon sx={{ minWidth: 32 }}><RadioButtonUncheckedIcon color="warning" fontSize="small" /></ListItemIcon>
                          <ListItemText primary={t.nome} />
                        </ListItem>
                      ))}
                    </List>
                  )}
                </AccordionDetails>
              </Accordion>
            </Grid>
          </Grid>

          {/* Bottone per aprire partite */}
          <Box sx={{ mb: 2, textAlign: 'center' }}>
            <Button variant="contained" onClick={() => navigate(`/partite?giornataId=${giornataId}`)} startIcon={<SportsSoccerIcon />}>Apri partite per giornata</Button>
          </Box>
          <Divider sx={{ my: 2 }} />
          {partite.length === 0 ? (
            <Alert severity="info">Nessuna partita ancora assegnata a questa giornata.</Alert>
          ) : (
            <Stack spacing={1}>
              {partite.map(p => (
                <Card key={p.id} variant="outlined" sx={{ p: 1 }}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <SportsSoccerIcon color="action" />
                    <Typography variant="body2" sx={{ flex: 1 }}>
                      {(teamsMap.get(p.teamCasaId) || `Team ${p.teamCasaId}`)} vs {(teamsMap.get(p.teamOspiteId) || `Team ${p.teamOspiteId}`)} • {p.data} {p.ore ? p.ore.substring(0, 5) : ''}
                    </Typography>
                    <Button size="small" onClick={() => navigate(`/partite/${p.id}`)}>Dettagli</Button>
                    <IconButton size="small" color="primary" onClick={() => handleOpenEdit(p)}><EditIcon /></IconButton>
                    <IconButton size="small" color="error" onClick={() => handleDeleteMatch(p.id)}><DeleteOutlineIcon /></IconButton>
                  </Stack>
                </Card>
              ))}
            </Stack>
          )}
        </CardContent>
      </Card>



      {/* Dialog Modifica Partita */}
      <Dialog open={isEditDialogOpen} onClose={handleCloseEdit} maxWidth="sm" fullWidth>
        <DialogTitle>Modifica Partita</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>Modifica i dettagli della partita selezionata.</DialogContentText>
          {editMatch && (
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <FormControl fullWidth size="small">
                  <InputLabel id="edit-casa">Team Casa</InputLabel>
                  <Select
                    labelId="edit-casa"
                    label="Team Casa"
                    value={editMatch.teamCasaId}
                    onChange={(e) => handleEditChange('teamCasaId', e.target.value)}
                  >
                    {teams.map(t => (
                      <MenuItem
                        key={t.id}
                        value={t.id}
                        disabled={String(t.id) === String(editMatch.teamOspiteId) || unavailableTeamsForEdit.has(Number(t.id))}
                      >
                        {t.nome} {unavailableTeamsForEdit.has(Number(t.id)) ? '(Impegnata)' : ''}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth size="small">
                  <InputLabel id="edit-ospite">Team Ospite</InputLabel>
                  <Select
                    labelId="edit-ospite"
                    label="Team Ospite"
                    value={editMatch.teamOspiteId}
                    onChange={(e) => handleEditChange('teamOspiteId', e.target.value)}
                  >
                    {teams.map(t => (
                      <MenuItem
                        key={t.id}
                        value={t.id}
                        disabled={String(t.id) === String(editMatch.teamCasaId) || unavailableTeamsForEdit.has(Number(t.id))}
                      >
                        {t.nome} {unavailableTeamsForEdit.has(Number(t.id)) ? '(Impegnata)' : ''}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="Data"
                  type="date"
                  fullWidth
                  size="small"
                  value={editMatch.data || ''}
                  onChange={(e) => handleEditChange('data', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="Ora"
                  type="time"
                  fullWidth
                  size="small"
                  value={editMatch.ore || ''}
                  onChange={(e) => handleEditChange('ore', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Luogo"
                  fullWidth
                  size="small"
                  value={editMatch.luogo || ''}
                  onChange={(e) => handleEditChange('luogo', e.target.value)}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Note"
                  fullWidth
                  size="small"
                  multiline
                  rows={2}
                  value={editMatch.note || ''}
                  onChange={(e) => handleEditChange('note', e.target.value)}
                />
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEdit}>Annulla</Button>
          <Button onClick={handleUpdateMatch} variant="contained" color="primary">Salva modifiche</Button>
        </DialogActions>
      </Dialog>
    </Box >
  );
}
